'use client';

import { useEffect } from 'react';

/**
 * Registers public/sw.js. Production-only: registering in dev would let the
 * worker's cache-first handling for /_next/static/** fight with Fast
 * Refresh's own asset churn. `updateViaCache: 'none'` makes the browser
 * always re-fetch sw.js itself on each check (never serve a stale copy from
 * HTTP cache), so new deployments are detected promptly via the browser's
 * normal update lifecycle -- no custom polling or forced-reload logic here.
 */
export default function ServiceWorkerRegistration({ enabled }: { enabled: boolean }) {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    let swRegistration: globalThis.ServiceWorkerRegistration | undefined;

    const register = async () => {
      try {
        swRegistration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
          updateViaCache: 'none',
        });
      } catch {
        // Registration failures (e.g. unsupported browser, blocked by policy)
        // must never break the app -- Ertzyx works fully without the worker.
      }
    };

    void register();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void swRegistration?.update();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled]);

  return null;
}
